/**
 * QuestionField Component
 *
 * Dumb component for rendering a single question field with its UI.
 * Handles the presentation layer while delegating all logic to parent components.
 */

import { FormFieldType } from '@ViewModels';
import { ISelectBoxOption } from '@WebComponents/SelectBox';
import { QuestionType } from '../../FormFieldWithOptions/questionTypes';
import { css } from 'aphrodite';
import * as React from 'react';
import { styleSheet } from '../styles';
import { QuestionFieldProps } from '../types';
import { QuestionContent } from './components/QuestionContent';
import { QuestionFieldErrorBoundary } from './components/QuestionFieldErrorBoundary';
import { QuestionHeader } from './components/QuestionHeader';
import { MODAL_HANDLER_TYPES, ModalHandlerType } from './constants/questionFieldConstants';
import { useCustomFieldManagement } from './hooks/useCustomFieldManagement';
import { useModalHandlers } from './hooks/useModalHandlers';
import { useTagManagement } from './hooks/useTagManagement';

/**
 * Pure presentation component for rendering a single question field
 */
export const QuestionField: React.FC<QuestionFieldProps> = React.memo(
	({
		field,
		questionIndex,
		isEdit,
		isDirty,
		hasError,
		hasBeenSaved,
		tagManagerProps,
		customFieldManagerProps,
		conditionalQuestionProps,
		conditionalVisibilityProps,
		onFieldUpdate,
		onFieldRemove,
		onLabelChange,
		onToggleOptional,
		onFieldTypeChange,
		onValidationTrigger,
		formFieldOptions,
		templateMode,
		children,
		questionErrors,
	}) => {
		// Determine visual state for the section container
		const shouldShowAsNotSaved = !hasBeenSaved || isDirty;

		// Check if this is an option field for tag functionality
		const isOptionField = field.fieldType === FormFieldType.MultipleOptions || field.fieldType === FormFieldType.Option;

		// Check if this field type supports custom field linking (only String and Option types)
		const supportsCustomField = field.fieldType === FormFieldType.String || field.fieldType === FormFieldType.Option;

		// Use custom hooks for complex logic
		const { hasTagPrefix, tagVisualization } = useTagManagement({
			tagManagerProps,
			field,
			isOptionField,
		});

		const { hasCustomField: hasExistingCustomField } = useCustomFieldManagement({
			customFieldManagerProps,
			field,
		});

		const canManageCustomField = !!customFieldManagerProps && supportsCustomField && !templateMode;

		const { setHandler, callHandler } = useModalHandlers<ModalHandlerType>();

		// Helper function for field type change rendering
		const onRenderTriggerTitle = React.useCallback(
			(selectedOption: ISelectBoxOption<QuestionType>): React.ReactNode => {
				return <span>{selectedOption.title as React.ReactNode}</span>;
			},
			[]
		);

		// Create handlers for SectionTitle dropdown.
		// `onFieldRemove` is optional — V2 sectioned editor withholds it for the
		// last question in a section. When undefined, pass undefined down so
		// SectionTitle suppresses the "Delete Question" row (and hides the
		// 3-dot icon if Delete was the only available action).
		const handleRemoveQuestion = React.useMemo(
			() => (onFieldRemove ? () => onFieldRemove(questionIndex) : undefined),
			[onFieldRemove, questionIndex]
		);

		// Modal handlers for dropdown actions
		const handleCreateTag = React.useCallback(() => {
			callHandler(MODAL_HANDLER_TYPES.CREATE_TAG);
		}, [callHandler]);

		const handleEditTag = React.useCallback(() => {
			callHandler(MODAL_HANDLER_TYPES.EDIT_TAG);
		}, [callHandler]);

		const handleCreateCustomField = React.useCallback(() => {
			callHandler(MODAL_HANDLER_TYPES.CREATE_CUSTOM_FIELD);
		}, [callHandler]);

		const handleEditCustomField = React.useCallback(() => {
			callHandler(MODAL_HANDLER_TYPES.EDIT_CUSTOM_FIELD);
		}, [callHandler]);

		return (
			<QuestionFieldErrorBoundary
				questionIndex={questionIndex}
				resetKeys={[field.id, questionIndex]}
				onError={(error, errorInfo) => {
					console.error('QuestionField Error:', error, errorInfo);
				}}
			>
				<section
					className={css(
						styleSheet.bottomSection,
						shouldShowAsNotSaved && styleSheet.bottomSectionNotSaved,
						hasBeenSaved && !isDirty && styleSheet.bottomSectionSaved,
						hasError && styleSheet.bottomSectionNoValue
					)}
					key={`${field.id}-question-${questionIndex}`}
				>
					<QuestionHeader
						questionIndex={questionIndex}
						styles={styleSheet.sectionTitle ? [styleSheet.sectionTitle] : []}
						onRemoveQuestion={handleRemoveQuestion}
						isOptionField={isOptionField}
						hasTagPrefix={hasTagPrefix}
						tagManagerProps={tagManagerProps}
						onCreateTag={templateMode ? undefined : handleCreateTag}
						onEditTag={templateMode ? undefined : handleEditTag}
						hasCustomField={canManageCustomField}
						hasExistingCustomField={hasExistingCustomField}
						customFieldManagerProps={customFieldManagerProps}
						onCreateCustomField={canManageCustomField ? handleCreateCustomField : undefined}
						onEditCustomField={canManageCustomField ? handleEditCustomField : undefined}
						onValidationTrigger={onValidationTrigger}
						onModalCreateTag={handler => setHandler(MODAL_HANDLER_TYPES.CREATE_TAG, handler)}
						onModalEditTag={handler => setHandler(MODAL_HANDLER_TYPES.EDIT_TAG, handler)}
						onModalCreateCustomField={handler => setHandler(MODAL_HANDLER_TYPES.CREATE_CUSTOM_FIELD, handler)}
						onModalEditCustomField={handler => setHandler(MODAL_HANDLER_TYPES.EDIT_CUSTOM_FIELD, handler)}
					/>

					<QuestionContent
						field={field}
						questionIndex={questionIndex}
						onToggleOptional={onToggleOptional}
						onLabelChange={onLabelChange}
						onRenderTriggerTitle={onRenderTriggerTitle}
						onFieldTypeChange={onFieldTypeChange}
						onFieldUpdate={onFieldUpdate}
						isEdit={isEdit}
						onAfterItem={tagVisualization}
						conditionalQuestionProps={conditionalQuestionProps}
						conditionalVisibilityProps={conditionalVisibilityProps}
						formFieldOptions={formFieldOptions}
						questionErrors={questionErrors}
						hasBeenSaved={hasBeenSaved}
					>
						{children}
					</QuestionContent>
				</section>
			</QuestionFieldErrorBoundary>
		);
	}
);

QuestionField.displayName = 'QuestionField';
